import { useState, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useChatStore } from '../../store/chatStore'
import { 
  MagnifyingGlassIcon,
  UserPlusIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  
  const { user } = useAuthStore()
  const { searchUsers, createChat } = useChatStore()

  useEffect(() => {
    if (!isOpen) {
      setQuery('')
      setSearchResults([])
      setSelectedUsers([])
    }
  }, [isOpen])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim() && query.length >= 2) {
        handleSearch()
      } else {
        setSearchResults([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  const handleSearch = async () => {
    setIsLoading(true)
    try {
      const results = await searchUsers(query)
      // Filter out current user from results
      setSearchResults(results.filter(u => u._id !== user._id))
    } catch (error) {
      console.error('Search error:', error)
      setSearchResults([])
    } finally {
      setIsLoading(false)
    }
  }

  const toggleUserSelection = (selectedUser) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === selectedUser._id)
      if (isSelected) {
        return prev.filter(u => u._id !== selectedUser._id)
      } else {
        return [...prev, selectedUser]
      }
    })
  }

  const handleCreateChat = async () => {
    if (selectedUsers.length === 0) return

    try {
      setIsLoading(true)
      
      if (selectedUsers.length === 1) {
        // Create direct message
        await createChat([selectedUsers[0]._id], false)
      } else {
        // Create group chat
        const chatName = selectedUsers.map(u => u.username).join(', ')
        await createChat(selectedUsers.map(u => u._id), true, chatName)
      }
      
      onClose()
    } catch (error) {
      console.error('Create chat error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Start New Chat
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users by username or display name..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              autoFocus
            />
          </div>
          
          {/* Search hint */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Type at least 2 characters to search for users
          </p>
        </div>

        {/* Selected Users */}
        {selectedUsers.length > 0 && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Selected ({selectedUsers.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map(selectedUser => (
                <div
                  key={selectedUser._id}
                  className="inline-flex items-center bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-3 py-1 rounded-full text-sm"
                >
                  <span>{selectedUser.username}</span>
                  <button
                    onClick={() => toggleUserSelection(selectedUser)}
                    className="ml-2 hover:text-primary-600 dark:hover:text-primary-300"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && query.length >= 2 ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="p-4 space-y-2">
              {searchResults.map(searchUser => {
                const isSelected = selectedUsers.some(u => u._id === searchUser._id)
                
                return (
                  <div
                    key={searchUser._id}
                    onClick={() => toggleUserSelection(searchUser)}
                    className={`
                      flex items-center p-3 rounded-lg cursor-pointer transition-colors
                      ${isSelected 
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0 mr-3">
                      {searchUser.avatar ? (
                        <img
                          src={searchUser.avatar}
                          alt={searchUser.username}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {searchUser.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {searchUser.username}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {searchUser.email}
                      </p>
                    </div>

                    {/* Selection Indicator */}
                    <div className="flex-shrink-0 ml-3">
                      {isSelected ? (
                        <CheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      ) : (
                        <UserPlusIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : query.length >= 2 && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
              <MagnifyingGlassIcon className="h-12 w-12 mb-2" />
              <p className="text-sm">No users found for "{query}"</p>
            </div>
          ) : query.length < 2 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
              <UserPlusIcon className="h-12 w-12 mb-2" />
              <p className="text-sm">Search for users to start a new chat</p>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {selectedUsers.length > 0 && (
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedUsers.length === 1 
                  ? 'Start direct message' 
                  : `Create group chat with ${selectedUsers.length} users`
                }
              </p>
              <button
                onClick={handleCreateChat}
                disabled={isLoading || selectedUsers.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlusIcon className="h-4 w-4 mr-1" />
                    Create Chat
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchModal